/**
 * Conjectures: proposals the model recalls no source stating, admitted as
 * records the rest of the app can test.
 *
 * Everything else in Pathways takes an idea apart. A conjecture pass is the one
 * place it proposes what the literature has not said — and the whole design of
 * this file is that proposing is the cheap half. What makes a proposal worth a
 * node is the contract it has to carry to land: what it departs from, the one
 * step no source the model knows takes, an observation where it and that
 * expectation DIFFER, and the cheapest test that would kill it. A proposal that
 * cannot fill those in is prose, and it is counted and dropped here rather than
 * drawn as though it were a thought.
 *
 * Nothing here decides whether a conjecture is new. No function in this file
 * reads `novelty`, `confidence` or `tension` to admit, order or drop anything,
 * and there is no field anywhere that says "novel". Whether anyone has already
 * said it is measured afterwards, by a search — never by the pass that proposed
 * it, and never by this module.
 *
 * Pure: no store, no clock read, no model. The caller hands in `now`.
 */

import type {
  ConjectureAdmission,
  ConjecturePrediction,
  ConjectureRecord,
  ConjectureRegistration,
  ModelStamp,
  ReasonNode,
  Session,
} from './types'
import { auditLive, normalizeNodeRef } from './types'
import { panelChip, type PanelChip } from './panel'
import { PANEL_CHIP_LABEL, STATUS_WORD } from './vocabulary'
import { distinctiveTerms } from './link'
import { foldText, textHash } from './similarity'
import { clamp } from './geometry'
import { makeNode } from './session'
import { RESEARCH_ITEM_CHARS, textRetention } from './textRetention'

/**
 * The generative moves, in the order the brief lists them.
 *
 * Named so the method is visible on the card and so a pass that proposed three
 * transplants can be counted as having proposed three transplants. A move is a
 * description of HOW a proposal was generated, never a grade of it.
 */
export const CONJECTURE_MOVES = [
  'anomaly',
  'assumption',
  'transplant',
  'regime',
  'unify',
  'revival',
  'measurement',
] as const

export type ConjectureMove = (typeof CONJECTURE_MOVES)[number]

/** How each move reads on a card. A method, stated as one; never a merit. */
export const CONJECTURE_MOVE_LABEL: Record<ConjectureMove, string> = {
  anomaly: 'Explains a result that does not fit',
  assumption: 'Drops an assumption the field holds fixed',
  transplant: 'Carries a structure over from another field',
  regime: 'Pushes one quantity past its usual range',
  unify: 'Gives two unexplained results one cause',
  revival: 'Revives an approach abandoned for a reason that lapsed',
  measurement: 'Measures what everyone infers',
}

/**
 * The most conjectures one pass may land.
 *
 * Three, and not the four or five the branching verbs allow: the brief asks for
 * "fewer is better than weaker", and a ceiling that invited padding would be the
 * instrument asking for exactly the prose this pass exists not to produce.
 * `reservedYieldFor` reads this, so the brief states it and admission enforces it
 * from one number.
 */
export const CONJECTURE_MAX_YIELD = 3

/**
 * Share of the SMALLER term set two conjectures must have in common to count as
 * one restated.
 *
 * An overlap coefficient rather than Jaccard, because a restatement usually adds
 * words to the one it restates, and Jaccard would read the padding as
 * difference. Set to lean toward calling a pair the same: a false duplicate costs
 * one paid proposal, while a false distinct one puts two copies of an idea on the
 * graph and lets a count of conjectures overstate what was proposed.
 */
export const CONJECTURE_DUPLICATE_OVERLAP = 0.6

/**
 * Below this many distinctive terms a conjecture's term set is too small to
 * compare, and no duplicate is declared. Two four-word labels sharing three words
 * are not the same proposal by any reading worth trusting.
 */
export const CONJECTURE_DUPLICATE_MIN_TERMS = 4

/** The most records the WHAT DOES NOT FIT block prints. Newest first. */
export const ANOMALY_BLOCK_MAX = 12

/** Anchors kept per conjecture. More than this is a list, not an explanation. */
export const CONJECTURE_MAX_ANCHORS = 6

/** Leads kept per conjecture; the schema asks for 0–3. */
export const CONJECTURE_MAX_LEADS = 3

/* ── registration ──────────────────────────────────────────────────────── */

/**
 * The fingerprint of a conjecture's wording: its claim and every part of its
 * prediction, folded.
 *
 * The prediction is inside the hash because it is the part a registration exists
 * to pin. A claim rewritten after the fact is visible as a rewrite; a prediction
 * quietly moved to match a result that came in would not be, without this.
 */
export function registrationHash(claim: string, prediction: ConjecturePrediction): string {
  return textHash(
    foldText(
      [
        claim,
        prediction.condition,
        prediction.observable,
        prediction.expected,
        prediction.consensusExpects,
      ].join('\n'),
    ),
  )
}

/** Whether the wording on screen is still the wording that was registered. */
export function registrationLive(n: Pick<ReasonNode, 'claim' | 'conjecture'>): boolean {
  const c = n.conjecture
  if (!c) return false
  return c.registered.contentHash === registrationHash(n.claim, c.prediction)
}

/**
 * A conjecture's record after its wording changed: the new wording registered
 * at `now`, the old registration kept, in order.
 *
 * Returns the record unchanged when nothing registered actually changed, so a
 * rewrite that only touched the reasoning does not mint a new date.
 */
export function reregister(
  record: ConjectureRecord,
  claim: string,
  now: number,
  prediction: ConjecturePrediction = record.prediction,
): ConjectureRecord {
  const contentHash = registrationHash(claim, prediction)
  // Compared by hash, never by object identity: an equal prediction in a new
  // object is the same wording, and minting a date for it would read as a
  // rewrite that never happened.
  if (contentHash === record.registered.contentHash) return record
  const registered: ConjectureRegistration = { at: now, contentHash }
  return {
    ...record,
    prediction,
    registered,
    registrations: [...(record.registrations ?? []), record.registered],
  }
}

/* ── admission ─────────────────────────────────────────────────────────── */

/** Why a returned conjecture did not land. Counted on the op, never swallowed. */
export type ConjectureRejection = 'incomplete' | 'undiscriminating' | 'duplicate'

/**
 * A prediction's outcome as the discrimination check compares it: case,
 * whitespace and punctuation folded away. "No change." and "no change" are one
 * prediction, and a check a full stop could defeat would admit exactly the lazy
 * case it exists to catch.
 */
const outcomeKey = (s: string): string =>
  foldText(s)
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()

/**
 * The mechanical question admission asks of a prediction: do the conjecture and
 * the expectation it departs from predict different outcomes?
 *
 * Deliberately a string check, and deliberately weak: it catches a response that
 * wrote the same outcome twice, and the audit catches the rest — a prediction
 * that differs in words and not in substance is an objection a reader can make,
 * never one a string comparison can.
 */
export function predictionDiscriminates(p: ConjecturePrediction): boolean {
  const expected = outcomeKey(p.expected)
  const consensus = outcomeKey(p.consensusExpects)
  return !!expected && !!consensus && expected !== consensus
}

/** The terms a conjecture is compared on for duplicates: its label and claim. */
export function conjectureTerms(n: { label: string; claim: string }): string[] {
  return distinctiveTerms({ label: n.label, claim: n.claim })
}

/** Whether two term sets restate one proposal. See `CONJECTURE_DUPLICATE_OVERLAP`. */
export function restates(a: readonly string[], b: readonly string[]): boolean {
  const small = a.length <= b.length ? a : b
  const large = new Set(a.length <= b.length ? b : a)
  if (small.length < CONJECTURE_DUPLICATE_MIN_TERMS) return false
  let shared = 0
  for (const t of small) if (large.has(t)) shared++
  return shared / small.length >= CONJECTURE_DUPLICATE_OVERLAP
}

export interface ConjectureAdmissionContext {
  /** Where every landed conjecture hangs. */
  parentId: string
  model: ModelStamp
  /** Ids an anchor may name: the nodes this graph holds when the pass began,
   *  and the sweep items the pass was shown. */
  resolvable: ReadonlySet<string>
  /**
   * Printed labels → stable ids. A sweep item is printed as `S1` and stored as
   * its own id, so the anchor survives the next sweep renumbering the block.
   */
  aliases?: ReadonlyMap<string, string>
  /**
   * Term sets a new conjecture must not restate: every conjecture already on the
   * graph, and every one admitted earlier in the same response. The caller owns
   * the list and appends each admitted conjecture's `terms` to it.
   */
  prior: readonly (readonly string[])[]
  now: number
}

export type ConjectureAdmit =
  | { admitted: true; node: ReasonNode; terms: string[]; anchorsDropped: number }
  | { admitted: false; reason: ConjectureRejection }

const isMove = (v: unknown): v is ConjectureMove =>
  typeof v === 'string' && (CONJECTURE_MOVES as readonly string[]).includes(v)

const unit = (v: unknown, dflt: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? clamp(v) : dflt

/**
 * One returned conjecture → a node we are willing to render, or the reason it
 * is not one.
 *
 * The precedence is the order a reader would ask: is the contract filled in,
 * does the prediction separate anything, and has this already been proposed
 * here. A proposal that fails the first is never compared on the third.
 *
 * EXPORTED FOR THE EVAL SEAM as well as for `reason.ts`, for the reason
 * `toNode` is: a harness that carried its own copy of these rules would score
 * its copy.
 */
export function toConjectureNode(raw: unknown, ctx: ConjectureAdmissionContext): ConjectureAdmit {
  if (!raw || typeof raw !== 'object') return { admitted: false, reason: 'incomplete' }
  const r = raw as Record<string, unknown>
  const retained = textRetention('admission')
  const label = retained.text('label', r.label, 90)
  const claim = retained.text('claim', r.claim)
  const departsFrom = retained.text('conjecture.departsFrom', r.departsFrom, RESEARCH_ITEM_CHARS)
  const leap = retained.text('conjecture.leap', r.leap, RESEARCH_ITEM_CHARS)
  const killTest = retained.text('conjecture.killTest', r.killTest, RESEARCH_ITEM_CHARS)
  const rawPrediction =
    r.prediction && typeof r.prediction === 'object'
      ? (r.prediction as Record<string, unknown>)
      : {}
  const prediction: ConjecturePrediction = {
    condition: retained.text(
      'conjecture.prediction.condition',
      rawPrediction.condition,
      RESEARCH_ITEM_CHARS,
    ),
    observable: retained.text(
      'conjecture.prediction.observable',
      rawPrediction.observable,
      RESEARCH_ITEM_CHARS,
    ),
    expected: retained.text(
      'conjecture.prediction.expected',
      rawPrediction.expected,
      RESEARCH_ITEM_CHARS,
    ),
    consensusExpects: retained.text(
      'conjecture.prediction.consensusExpects',
      rawPrediction.consensusExpects,
      RESEARCH_ITEM_CHARS,
    ),
  }

  if (
    !label ||
    !claim ||
    !isMove(r.move) ||
    !departsFrom ||
    !leap ||
    !killTest ||
    !prediction.condition ||
    !prediction.observable ||
    !prediction.expected ||
    !prediction.consensusExpects
  ) {
    return { admitted: false, reason: 'incomplete' }
  }
  if (!predictionDiscriminates(prediction)) return { admitted: false, reason: 'undiscriminating' }

  const terms = conjectureTerms({ label, claim })
  if (ctx.prior.some((p) => restates(terms, p))) return { admitted: false, reason: 'duplicate' }

  const offered = Array.isArray(r.anchors)
    ? r.anchors
        .filter((a): a is string => typeof a === 'string')
        .map(normalizeNodeRef)
        .map((a) => ctx.aliases?.get(a) ?? a)
    : []
  const anchors: string[] = []
  let anchorsDropped = 0
  for (const id of offered) {
    if (!id) continue
    // The target itself is not an anomaly the conjecture explains — it is what
    // the conjecture is about. Dropped silently rather than counted, because the
    // brief invites exactly that reading of "the items above".
    if (id === ctx.parentId) continue
    if (!ctx.resolvable.has(id)) {
      anchorsDropped++
      continue
    }
    if (!anchors.includes(id) && anchors.length < CONJECTURE_MAX_ANCHORS) anchors.push(id)
  }

  const record: ConjectureRecord = {
    move: r.move,
    departsFrom,
    leap,
    prediction,
    killTest,
    anchors,
    priorArtLeads: retained.list('conjecture.priorArtLeads', r.priorArtLeads, CONJECTURE_MAX_LEADS),
    registered: { at: ctx.now, contentHash: registrationHash(claim, prediction) },
  }

  const node = makeNode({
    parentId: ctx.parentId,
    label,
    claim,
    reasoning: retained.text('reasoning', r.reasoning),
    kind: 'conjecture',
    // Defaults only for a response that broke the schema. A conjecture's honest
    // range is low, so a missing confidence defaults low rather than to the
    // ordinary node's middle.
    confidence: unit(r.confidence, 0.2),
    novelty: unit(r.novelty, 0.5),
    tension: unit(r.tension, 0.2),
    evidence: retained.list('evidence', r.evidence, 3),
    nextSteps: [],
    // A proposal is never a dead end on arrival: it has not been tested yet.
    terminal: false,
    conjecture: record,
    origin: 'conjecture',
    model: ctx.model,
    createdAt: ctx.now,
    ...retained.fields(),
  })
  return { admitted: true, node, terms, anchorsDropped }
}

/**
 * One response's admission, object by object, as the pass and the eval both run
 * it.
 *
 * The ceiling first and by position — no structured-output dialect can carry it,
 * so this is where it holds — then `toConjectureNode`, then the bookkeeping that
 * makes each admitted conjecture part of the duplicate check for the ones after
 * it. ONE definition, deliberately: `runConjecture` streams objects into it and
 * `evals/conjectures` replays recorded ones through it, and a harness that
 * carried its own copy of this loop would score its copy.
 */
export function conjectureAdmitter(opts: {
  parentId: string
  model: ModelStamp
  resolvable: ReadonlySet<string>
  aliases?: ReadonlyMap<string, string>
  /** Term sets already on the graph. Owned by the admitter from here on. */
  prior: string[][]
  ceiling: number
  now: () => number
}): { admission: ConjectureAdmission; push: (raw: unknown) => ReasonNode | null } {
  const admission = emptyAdmission()
  return {
    admission,
    push(raw) {
      admission.returned++
      if (admission.landed >= opts.ceiling) {
        admission.overCeiling++
        return null
      }
      const r = toConjectureNode(raw, {
        parentId: opts.parentId,
        model: opts.model,
        resolvable: opts.resolvable,
        ...(opts.aliases ? { aliases: opts.aliases } : {}),
        prior: opts.prior,
        now: opts.now(),
      })
      if (!r.admitted) {
        admission[r.reason]++
        return null
      }
      admission.landed++
      admission.anchorsDropped += r.anchorsDropped
      opts.prior.push(r.terms)
      return r.node
    },
  }
}

/** A zeroed tally, for a pass that is about to count its response. */
export function emptyAdmission(): ConjectureAdmission {
  return {
    returned: 0,
    landed: 0,
    incomplete: 0,
    undiscriminating: 0,
    duplicate: 0,
    overCeiling: 0,
    anchorsDropped: 0,
  }
}

/**
 * The admission tally as one sentence, for a toast and for the op's row.
 *
 * Counts only: "1 predicted nothing the expectation it departs from would not"
 * is a fact anybody can re-derive from the response, where "1 was weak" would be
 * this tool grading its own inputs.
 */
export function admissionSentence(a: ConjectureAdmission): string {
  const parts = [`${a.returned} returned`, `${a.landed} landed`]
  if (a.incomplete) parts.push(`${a.incomplete} left part of the contract empty`)
  if (a.undiscriminating)
    parts.push(
      `${a.undiscriminating} predicted nothing the expectation it departs from would not also predict`,
    )
  if (a.duplicate)
    parts.push(`${a.duplicate} restated ${a.duplicate === 1 ? 'one' : 'ones'} already here`)
  if (a.overCeiling) parts.push(`${a.overCeiling} came past the ceiling of ${CONJECTURE_MAX_YIELD}`)
  if (a.anchorsDropped)
    parts.push(
      `${a.anchorsDropped} anchor${a.anchorsDropped === 1 ? '' : 's'} named no node on this graph and no sweep item the pass was shown`,
    )
  return parts.join(' · ')
}

/* ── what does not fit ─────────────────────────────────────────────────── */

// In a leaf module of its own, so the coach can read the same list — see there.
export {
  anomalyRecords,
  mixedShape,
  type AnomalyKind,
  type AnomalyRecord,
  type MixedShape,
} from './anomaly'

/* ── standing ──────────────────────────────────────────────────────────── */

/**
 * What a conjecture has been through, as facts read off records.
 *
 * Every field is a count or a stored value, never a score, and there is no
 * field that combines them — "survived three instruments" would be a composite,
 * and a composite is the one thing a conjecture's card must not print. A reader
 * holds the facts side by side and draws the line.
 */
export interface ConjectureStanding {
  /** Challenge children and contradictions hanging directly off it. */
  attacks: number
  /** The live cross-examination's verdict, and whose it was. */
  audit: { verdict: 'objection' | 'pass' | 'insufficient-context'; by: string } | null
  /** Which of the panel chip's three states a stored panel is (`panelChip`), `null`
   *  when no panel ran. Three states and not a split flag: a panel that did not
   *  split is not a panel that agreed, since one decided lens has nothing to agree
   *  with (D34's third amendment). The ledger read the flag, and said "the panel
   *  agreed" of exactly that panel (D291). */
  panel: PanelChip | null
  /** Experiment children proposed against it. */
  tests: number
  /** The recorded result's bearing, when there is one. */
  result: 'supported' | 'refuted' | 'inconclusive' | null
  /** Whether the wording on screen is the wording that was registered. */
  registrationLive: boolean
  /** Registrations before the current one: rewrites after it was set down. */
  rewrites: number
}

export function conjectureStanding(session: Session, n: ReasonNode): ConjectureStanding {
  let attacks = 0
  let tests = 0
  for (const id of session.order) {
    const c = session.nodes[id]
    if (!c || c.parentId !== n.id) continue
    if (c.origin === 'challenge' || c.kind === 'contradiction') attacks++
    if (c.kind === 'experiment') tests++
  }
  return {
    attacks,
    audit: n.audit && auditLive(n) ? { verdict: n.audit.verdict, by: n.audit.model.name } : null,
    panel: n.panel ? panelChip(n.panel) : null,
    tests,
    result: n.outcome?.bearing ?? null,
    registrationLive: registrationLive(n),
    rewrites: n.conjecture?.registrations?.length ?? 0,
  }
}

/** What a cross-examination's verdict says of a conjecture, after its auditor's
 *  name. "Proposed a criticism", never "objected": a review's criticism stays
 *  proposed until its argument is checked, and the report's own list of reviews
 *  calls it a proposed criticism a few sections up. */
const AUDIT_FACT: Record<NonNullable<ConjectureStanding['audit']>['verdict'], string> = {
  objection: 'proposed a criticism',
  pass: 'found no specific objection',
  'insufficient-context': 'lacked the context to review it',
}

/**
 * What a conjecture has been through on the graph, as the clauses its card in the
 * report's Insights section prints after "Through so far" — so the Overview's
 * fold, the Markdown and the PDF say them from this one derivation (D141, D168,
 * D291).
 *
 * The old ledger's line, less what the card already prints on lines of its own:
 * the attacks hanging off it, a live cross-examination's verdict and whose it was,
 * what a panel's lenses did, and the tests proposed under it. A zero is said, not
 * dropped — "not challenged yet" is a fact, and a clause left out reads as nothing
 * to report. Each clause is a count or a stored value; none combines them, none is
 * a score, and nothing reads them to order a card (D281, D282). None prints a
 * self-rating, so a node carried from a conjecture, which has none, changes
 * nothing here.
 *
 * What the ledger's line said that is not here, because the card says it already
 * and one card should not say a thing twice: the prior-art reading and its day,
 * and any page dated after the registration (the card's prior-art line and its
 * sources); what an executable test read, or that it has not run, and a recorded
 * result (their own lines); what it departs from, where that is on the graph (the
 * carried line); and who proposed it (the registration line).
 */
export function throughSoFar(session: Session, n: ReasonNode): string[] {
  const st = conjectureStanding(session, n)
  const facts = [
    st.attacks
      ? `${st.attacks} attack${st.attacks === 1 ? ' hangs' : 's hang'} off it`
      : STATUS_WORD.notChallenged.toLowerCase(),
  ]
  if (st.audit) facts.push(`${st.audit.by} ${AUDIT_FACT[st.audit.verdict]}`)
  // The panel chip's own words, lower-cased mid-line: one state, one word (D166).
  if (st.panel) facts.push(PANEL_CHIP_LABEL[st.panel].toLowerCase())
  facts.push(
    st.tests
      ? `${st.tests} test${st.tests === 1 ? '' : 's'} proposed under it`
      : STATUS_WORD.noTestProposed.toLowerCase(),
  )
  return facts
}

/**
 * A recorded result, read against a conjecture's prediction.
 *
 * "Went the way the prediction said", never "confirmed": the `outcome` brief's
 * own rule is that one observation is one observation, and a conjecture whose
 * prediction came true once has had one prediction come true once. Each reading
 * is a status word, so the table is defined in core/vocabulary.ts, built from the
 * track record's own clauses, and re-exported here for the surfaces that read the
 * rest of a conjecture's words from this file (D287).
 */
export { RESULT_READING } from './vocabulary'

/** A last word whose full stop is its own: dotted initials, or a common short form. */
const ABBREVIATED = /(?:^|[\s(])(?:(?:[a-z]\.){2,}|(?:et al|etc|vs|cf|approx)\.)$/i

/**
 * A part of a sentence, as a response may have written it: as a sentence.
 *
 * The schema asks for each part of a prediction as a clause, and a response can
 * still write capital-and-full-stop sentences — the live pass on 2 October 2026
 * did, for every part, and the card read "…state of health., TX-9 … before the
 * knee. shows Ratio…". Every template that prints a prediction closes the parts
 * itself, with a comma, "shows", a dash or a full stop, so a part's own closing
 * full stop is dropped where the sentence is built. Never where it is stored: the
 * registration hash reads the stored words, and a part rewritten on the way in
 * would re-register every conjecture that wrote one.
 *
 * Kept: an ellipsis, and the full stop of an abbreviation, which belongs to the
 * word rather than the sentence. The case is left alone. A capital mid-sentence
 * reads oddly, but a lower-cased "Li-ion" or researcher's name is wrong, and
 * nothing here can tell a sentence's first word from either.
 */
export function asClause(text: string): string {
  const t = text.trim()
  if (!t.endsWith('.') || t.endsWith('..') || ABBREVIATED.test(t)) return t
  return t.slice(0, -1).trimEnd()
}

/** A sentence built from parts, closed with one full stop however its last part ended. */
export const closeSentence = (text: string): string => (/[.?!]$/.test(text) ? text : `${text}.`)

/** Every part of a prediction, made fit to sit inside a sentence (`asClause`). */
export function predictionClauses(p: ConjecturePrediction): ConjecturePrediction {
  return {
    condition: asClause(p.condition),
    observable: asClause(p.observable),
    expected: asClause(p.expected),
    consensusExpects: asClause(p.consensusExpects),
  }
}

/**
 * The prediction as one sentence a reader can hold the world up against: the
 * condition, the observable, what the conjecture expects — and what the
 * expectation it departs from says instead, which is the half that makes it a
 * prediction of THIS conjecture. One wording for every surface that prints it as
 * plain text: the card, the report's section and the voice. The Markdown export,
 * the briefs that show a model the prediction and the Research Lab contract build
 * their own sentences from the same `predictionClauses`.
 */
export function predictionSentence(n: Pick<ReasonNode, 'conjecture'>): string {
  const c = n.conjecture
  if (!c) return ''
  const p = predictionClauses(c.prediction)
  return closeSentence(
    `If ${p.condition}, ${p.observable} shows ${p.expected} — where the expectation it departs from says ${p.consensusExpects}`,
  )
}

/** Every conjecture on a graph, newest registration first. */
export function conjecturesOf(session: Session): ReasonNode[] {
  const out: ReasonNode[] = []
  for (const id of session.order) {
    const n = session.nodes[id]
    if (n?.kind === 'conjecture' && n.conjecture) out.push(n)
  }
  return out.sort(
    (a, b) =>
      b.conjecture!.registered.at - a.conjecture!.registered.at ||
      session.order.indexOf(b.id) - session.order.indexOf(a.id),
  )
}

/* ── the expectation it departs from ────────────────────────────────── */

/** Words of the expectation kept in its label. A canvas caption, not a claim. */
export const CONSENSUS_LABEL_WORDS = 6

/** The label a carried expectation wears: its first few words, marked as cut. */
export function consensusLabel(departsFrom: string): string {
  const words = departsFrom
    .trim()
    .replace(/[.;:!?]+$/, '')
    .split(/\s+/)
    .filter(Boolean)
  const head = words.slice(0, CONSENSUS_LABEL_WORDS).join(' ')
  return words.length > CONSENSUS_LABEL_WORDS ? `${head}…` : head
}

/** The node carrying a conjecture's "departs from", when one has been landed. */
export function consensusNodeOf(session: Session, conjectureId: string): ReasonNode | undefined {
  for (const id of session.order) {
    const n = session.nodes[id]
    if (n?.carriedFrom === conjectureId) return n
  }
  return undefined
}

/**
 * A conjecture's "departs from", as an ordinary evidence node beside it — so the
 * source check can be aimed at the thing the conjecture says specialists expect.
 *
 * The prior-art search asks whether the departure has already been said; this is
 * its counterpart, asking whether the thing departed from is actually held. A
 * strawman's "departure" is from nothing anybody believes, and a check on this
 * node is where that shows.
 *
 * Landing costs nothing: the text was paid for with the conjecture. It hangs
 * where the conjecture hangs, carries that conjecture's model stamp because that
 * model wrote the sentence, and is marked `carriedFrom` because no model rated
 * it — its ratings are neutral placeholders that every grading and comparing
 * reader skips (see `ReasonNode.carriedFrom`). Null for anything that is not a
 * conjecture.
 */
export function toConsensusNode(cj: ReasonNode, now: number): ReasonNode | null {
  const c = cj.conjecture
  if (cj.kind !== 'conjecture' || !c || cj.parentId === null) return null
  const by = cj.model ? cj.model.name : 'the model that proposed it'
  return makeNode({
    parentId: cj.parentId,
    kind: 'evidence',
    label: consensusLabel(c.departsFrom),
    claim: c.departsFrom,
    reasoning: `What the insight “${cj.label}” departs from, as ${by} set it down with that insight. Put on the graph so a source check can ask whether specialists actually hold it. No model rated this node.`,
    // Placeholders, never ratings: nothing reads them to grade or compare, and
    // the inspector prints none. Novelty zero is also a description — this is,
    // by the conjecture's own account, what a specialist would say first.
    confidence: 0.5,
    novelty: 0,
    tension: 0,
    evidence: [],
    nextSteps: [],
    terminal: false,
    origin: 'conjecture',
    carriedFrom: cj.id,
    ...(cj.model ? { model: cj.model } : {}),
    createdAt: now,
  })
}

/** The term sets of every conjecture already on a graph — admission's `prior`. */
export function conjectureTermsOnGraph(session: Session): string[][] {
  const out: string[][] = []
  for (const id of session.order) {
    const n = session.nodes[id]
    if (n?.kind === 'conjecture') out.push(conjectureTerms(n))
  }
  return out
}
